import { Button, Heading, Text, VStack } from "@astryxdesign/core";
import * as stylex from "@stylexjs/stylex";
import { Truck } from "lucide-react";
import { Navigate, useNavigate } from "react-router";
import { FullScreenLoader } from "@/components/spinner";
import { useSession } from "@/services/auth0/session";
import {
  PendingInvitePrompt,
  usePendingInvite,
} from "@/screens/pending-invite";

const styles = stylex.create({
  screen: {
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 32,
    paddingInline: 24,
    textAlign: "center",
  },
  brandMark: {
    display: "flex",
    width: 56,
    height: 56,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    backgroundColor: "var(--color-background-inverted)",
    color: "var(--color-on-dark)",
  },
  actionWrap: {
    width: "100%",
    maxWidth: "20rem",
  },
});

export function LoginScreen() {
  const { isLoading, isLoggedIn, login } = useSession();

  if (isLoading) return <FullScreenLoader />;
  if (isLoggedIn) return <Navigate to="/" replace />;

  return (
    <div {...stylex.props(styles.screen)}>
      <VStack align="center" gap={3}>
        <div {...stylex.props(styles.brandMark)}>
          <Truck size={28} />
        </div>
        <VStack align="center" gap={1}>
          <Heading level={1}>EZX Operator</Heading>
          <Text size="sm" color="secondary">
            Sign in to see your assigned trips.
          </Text>
        </VStack>
      </VStack>
      <div {...stylex.props(styles.actionWrap)}>
        <Button
          variant="primary"
          size="lg"
          width="100%"
          label="Sign in"
          onClick={() => login()}
        />
      </div>
    </div>
  );
}

/**
 * Where a driver lands when they have no operator access yet.
 *
 * Two very different reasons produce that state, and they need different
 * screens: someone who was never invited has nothing to do here, while someone
 * with a waiting invite is one tap from being in. Showing the second group a
 * dead end was the actual complaint behind the PAL-first invite work.
 */
export function NoAccessScreen() {
  const { logout } = useSession();
  const navigate = useNavigate();
  const invite = usePendingInvite();

  if (invite.isLoading) return <FullScreenLoader />;

  return (
    <div {...stylex.props(styles.screen)}>
      {invite.data ? (
        <PendingInvitePrompt
          invite={invite.data}
          onAccepted={() => navigate("/", { replace: true })}
        />
      ) : (
        <VStack align="center" gap={1}>
          <Heading level={1}>No operator access</Heading>
          <Text size="sm" color="secondary">
            This account isn't an operator. Ask dispatch to grant the OPERATOR
            role, then sign in again.
          </Text>
        </VStack>
      )}
      <Button variant="secondary" label="Sign out" onClick={() => logout()} />
    </div>
  );
}
